import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './styles.module.less';
import { useScanVerificationContext } from '../../scan-verification-context-provider';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../../../../tracking/tracking-constants';

import type { CellProps } from './types';

const ActionsCell = ({ row }: CellProps) => {
  const { verifyItem, resetItemCount, isItemVerified, getVerifiedCount, isSubmitting } = useScanVerificationContext();
  const item = row.original;
  const workflowTracker = useTrackWorkflow(Workflows.SCAN_TO);

  const isVerified = isItemVerified(item.fulfillmentPlanItemId);
  const verifiedCount = getVerifiedCount(item.fulfillmentPlanItemId);

  const verify = useCallback(() => {
    workflowTracker(WorkflowSteps.ITEM_VERIFIED_MANUAL, {
      itemId: item.fulfillmentPlanItemId,
      verifiedCount: item.quantity,
    });

    verifyItem(item.fulfillmentPlanItemId, item.quantity);
  }, [verifyItem, item.fulfillmentPlanItemId, item.quantity, workflowTracker]);

  const clear = useCallback(() => {
    resetItemCount(item.fulfillmentPlanItemId);
  }, [resetItemCount, item.fulfillmentPlanItemId]);

  return (
    <FlexRow gap={Gap.Space8}>
      <Button disabled={isVerified || isSubmitting} onClick={verify} theme={THEME_CONSTANTS.secondary}>
        <FormattedMessage {...messages.verifyButton} />
      </Button>
      <Button disabled={verifiedCount === 0 || isSubmitting} onClick={clear} theme={THEME_CONSTANTS.tertiary}>
        <FormattedMessage {...messages.clearButton} />
      </Button>
    </FlexRow>
  );
};

export const createActionsColumnDefinition = createColumnDefinitionBuilder({
  accessor: 'actions',
  Header: <FormattedMessage {...messages.actionsColumnHeader} />,
  Cell: ActionsCell,
  className: style['centered-cell'],
  minWidth: 160,
});
